import React, { useState, useMemo } from 'react';

/**
 * usePagination — IMP 8.
 *
 * Page-size-aware client-side pagination hook. Returns the currently
 * visible slice plus a `SeeMoreButton` you can drop directly under
 * the list. Default page size is 25 (user spec).
 */
export const DEFAULT_PAGE_SIZE = 25;

export function usePagination(items, pageSize = DEFAULT_PAGE_SIZE) {
  const [shown, setShown] = useState(pageSize);
  const total = (items || []).length;
  const visible = useMemo(() => (items || []).slice(0, shown), [items, shown]);
  const hasMore = shown < total;
  const remaining = Math.max(0, total - shown);
  const showMore = () => setShown((n) => n + pageSize);
  const reset = () => setShown(pageSize);
  return { visible, hasMore, remaining, total, shown, showMore, reset };
}

/**
 * SeeMoreButton — drop-in pagination footer for any list.
 *
 * Props:
 *   hasMore: boolean
 *   remaining: number
 *   onClick: () => void
 *   pageSize?: number  (used in the button label, defaults to 25)
 *   testid?: string    (data-testid suffix so each list has a unique id)
 */
export function SeeMoreButton({ hasMore, remaining, onClick, pageSize = DEFAULT_PAGE_SIZE, testid = 'list' }) {
  if (!hasMore) return null;
  const nextBatch = Math.min(remaining, pageSize);
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '0.75rem 0' }}>
      <button
        type="button"
        onClick={onClick}
        data-testid={`see-more-${testid}`}
        style={{ background: '#f1f5f9', color: '#0f172a',
                 border: '1px solid #e2e8f0', borderRadius: 999,
                 padding: '0.5rem 1.1rem', fontWeight: 600,
                 fontSize: '0.85rem', cursor: 'pointer' }}>
          + See more · {nextBatch} of {remaining} remaining
      </button>
    </div>
  );
}

/**
 * 2026-02 — DashboardSeeMore.
 *
 * Used by dashboard sections that cap at 6 items. ALWAYS visible as a
 * navigation pill so the agent can jump to the dedicated full-list page
 * even when the dashboard already shows every record. Label adapts:
 *   - records > 6 visible → "+ See more · N more →"
 *   - all records visible (≤ 6) → "View all (N) →"
 */
export function DashboardSeeMore({ to, totalRemaining = 0, totalCount, testid = 'section' }) {
  // Hide entirely only when there are literally zero records to view.
  const allCount = typeof totalCount === 'number'
    ? totalCount
    : (totalRemaining || 0);
  if (allCount <= 0 && totalRemaining <= 0) return null;
  const hasMore = totalRemaining > 0;
  const label = hasMore
    ? `+ See more · ${totalRemaining} more \u2192`
    : `View all${typeof totalCount === 'number' ? ` (${totalCount})` : ''} \u2192`;
  return (
    <div style={{ display: 'flex', justifyContent: 'center',
                  padding: '0.85rem 0 0.25rem' }}>
      <a
        href={to}
        data-testid={`see-more-${testid}`}
        style={{ background: hasMore ? '#0073EA' : '#fff',
                 color: hasMore ? '#fff' : '#0073EA',
                 border: hasMore ? 'none' : '1px solid #0073EA',
                 borderRadius: 999,
                 padding: '0.55rem 1.4rem', fontWeight: 700,
                 fontSize: '0.85rem', textDecoration: 'none',
                 boxShadow: hasMore ? '0 2px 6px rgba(0,115,234,0.25)' : 'none' }}>
        {label}
      </a>
    </div>
  );
}
